import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Regexp from '../../constants/regexp';
import { registerFootballTournamentRegistration } from '../../services/e-cup.service';

import './ECupApplyChallengeForm.component.scss';
import TermsAgreementModal from '../../modal-pages/terms-agreement-modal/TermsAgreementModal.component';

const PLAYER_COUNT = 3;
const KIT_SIZES = ['xs', 's', 'm', 'l', 'xl', 'xxl'];

function emptyPlayer() {
    return {
        firstName: '',
        lastName: '',
        dob: '',
        email: '',
        phone: '',
        address: '',
        instagram: '',
        tikTok: '',
        youTube: '',
        footballTeam: '',
        jerseySize: '',
        shortsSize: '',
        bootSize: ''
    };
}
function getDefaultValues() {
    return {
        teamGender: null,
        teamName: '',
        players: Array.from({ length: PLAYER_COUNT }, () => emptyPlayer()),
        confirmPassport: null,
        confirmCriminal: null,
        confirmTravel: null,
        confirmNoDoubleEntry: null,
        signupForNews: false,
        agreeTerms: false
    };
}
function validateAge18To25(value) {
    if (!value) return true;
    const inputDate = new Date(value);
    if (Number.isNaN(inputDate.getTime())) return 'Invalid date.';
    const today = new Date();
    let age = today.getFullYear() - inputDate.getFullYear();
    const monthDiff = today.getMonth() - inputDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate())) {
        age -= 1;
    }
    return age >= 18 && age <= 25 ? true : 'Must be between 18 and 25 years old.';
}

function normalizeServerFieldPath(key) {
    if (!key || typeof key !== 'string') {
        return key;
    }
    return key.replace(/\[(\d+)\]/g, '.$1').replace(/\]\./g, '.');
}

function buildCupRegistrationPayload(data) {
    return {
        teamGender: data.teamGender,
        teamName: data.teamName?.trim() ?? '',
        players: (data.players || []).map((p) => ({
            firstName: p.firstName?.trim() ?? '',
            lastName: p.lastName?.trim() ?? '',
            dob: p.dob,
            email: p.email?.trim() ?? '',
            phone: p.phone?.trim() ?? '',
            address: p.address?.trim() ?? '',
            instagram: (p.instagram && String(p.instagram).trim()) || '',
            tikTok: (p.tikTok && String(p.tikTok).trim()) || '',
            youTube: (p.youTube && String(p.youTube).trim()) || '',
            footballTeam: (p.footballTeam && String(p.footballTeam).trim()) || '',
            jerseySize: p.jerseySize,
            shortsSize: p.shortsSize,
            bootSize: p.bootSize === '' || p.bootSize == null ? undefined : Number(p.bootSize)
        })),
        confirmPassport: data.confirmPassport,
        confirmCriminal: data.confirmCriminal,
        confirmTravel: data.confirmTravel,
        confirmNoDoubleEntry: data.confirmNoDoubleEntry,
        signupForNews: !!data.signupForNews,
    };
}

function extractCupRegistrationApiErrors(err) {
    const d = err?.response?.data;
    const fieldMap = {};
    let rootMessage = '';

    if (d && typeof d === 'object') {
        if (typeof d.message === 'string' && d.message.trim()) {
            rootMessage = d.message.trim();
        }
        if (d.details && typeof d.details === 'object' && !Array.isArray(d.details)) {
            for (const [rawKey, val] of Object.entries(d.details)) {
                const msg = typeof val === 'string' ? val : (val && typeof val.message === 'string' ? val.message : '');
                if (msg) {
                    fieldMap[normalizeServerFieldPath(rawKey)] = msg;
                }
            }
        }
        if (Object.keys(fieldMap).length === 0 && typeof d.path === 'string' && typeof d.message === 'string' && d.message.trim()) {
            fieldMap[normalizeServerFieldPath(d.path)] = d.message.trim();
            rootMessage = '';
        }
    }

    if (Object.keys(fieldMap).length > 0) {
        rootMessage = '';
    }
    if (Object.keys(fieldMap).length === 0 && !rootMessage) {
        rootMessage = 'Something went wrong. Please try again later.';
    }

    return { fieldMap, rootMessage };
}


function ECupApplyChallengeForm({ onClose, onSuccess }) {
    const defaultValues = useMemo(() => getDefaultValues(), []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formRootError, setFormRootError] = useState(null);
    const [expandedPlayerIndexes, setExpandedPlayerIndexes] = useState([0]);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset, watch, setError, clearErrors, setValue } = useForm({
        mode: 'onSubmit',
        defaultValues
    });

    const isTermsAccepted = !!watch('agreeTerms');
    const isSignupForNews = !!watch('signupForNews');

    const expandPlayerTabsForErrors = (playerErrors) => {
        if (!playerErrors || typeof playerErrors !== 'object') {
            return;
        }
        const indexes = Object.keys(playerErrors).map((key) => Number(key)).filter((index) => Number.isInteger(index));
        if (!Array.isArray(indexes) || indexes.length === 0) {
            return;
        }
        setExpandedPlayerIndexes((prev) => Array.from(new Set([...prev, ...indexes])).sort((a, b) => a - b));
    };

    const onAgreeTermsCheckboxClick = (event) => {
        event.preventDefault();
        if (!isTermsAccepted) {
            event.target.checked = false;
            setIsTermsModalOpen(true);
            return;
        }
        setValue('agreeTerms', false, { shouldValidate: true, shouldDirty: true });
    };

    const onSubmit = async (data) => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setFormRootError(null);
        clearErrors();
        try {
            const payload = buildCupRegistrationPayload(data);
            const apiRes = await registerFootballTournamentRegistration(payload);
            const response = apiRes.data ?? {};
            const code = response.code;
            const explicitSuccess = code === 'CREATED' || code === 'OK';
            const explicitError = typeof code === 'string' && code.startsWith('E_');
            const httpOk = apiRes.status >= 200 && apiRes.status < 300;

            if (explicitSuccess || (httpOk && !explicitError)) {
                reset(getDefaultValues());
                setExpandedPlayerIndexes([0]);
                onClose?.();
                onSuccess?.();
            } else {
                setFormRootError(response?.message || 'Unable to submit your registration. Please try again.');
            }
        } catch (err) {
            const { fieldMap, rootMessage } = extractCupRegistrationApiErrors(err);
            Object.entries(fieldMap).forEach(([path, message]) => {
                setError(path, { type: 'server', message });
            });
            if (rootMessage) {
                setFormRootError(rootMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const onInvalidSubmit = (submitErrors) => {
        expandPlayerTabsForErrors(submitErrors?.players);
    };


    return (
        <form className='apply-challenge-form'
            autoComplete='off'
            onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
        >
            <div className='apply-challenge-intro'>
                <h3 className='apply-challenge-intro-title'>
                    THINK YOU HAVE WHAT IT TAKES TO WIN THE 3V3 CHALLENGE AND WIN TICKETS TO THE FIFA WORLD CUP 2026?
                </h3>
                <p className='apply-challenge-intro-copy'>
                    SIGN UP BELOW YOUR 3 PLAYERS FOR THE CHANCE TO ENTER.
                    <br />
                    Please note you must be between the ages of 18 and 25 to sign up. All identification will be check before the event to confirm details before the event date. Any incorrect details entered will disqualify the entire.
                </p>
            </div>

            {formRootError && (
                <div className='apply-challenge-form-server-alert' role='alert'>
                    <span className='apply-challenge-form-server-alert-eyebrow'>Error</span>
                    <p className='apply-challenge-form-server-alert-text'>{formRootError}</p>
                </div>
            )}

            <div className='apply-challenge-field-group'>
                <div className='apply-challenge-radio-row apply-challenge-main-type' role='radiogroup'>
                    <label className={`apply-challenge-radio-label ${watch('teamGender') === 'male' ? 'apply-challenge-radio-label--active' : ''}`}>
                        <input className='apply-challenge-radio-input'
                            type='radio' value='male'
                            {...register('teamGender', { required: 'Please select team type.' })}
                        />
                        <span className='apply-challenge-radio-text'>
                            MEN'S TOURNAMENT*
                        </span>
                    </label>
                    <label className={`apply-challenge-radio-label ${watch('teamGender') === 'female' ? 'apply-challenge-radio-label--active' : ''}`}>
                        <input className='apply-challenge-radio-input'
                            type='radio' value='female'
                            {...register('teamGender', { required: 'Please select team type.' })}
                        />
                        <span className='apply-challenge-radio-text'>
                            WOMEN'S TOURNAMENT*
                        </span>
                    </label>
                </div>
                {errors.teamGender && (
                    <h4 className='apply-challenge-err-msg'>
                        {errors.teamGender.message}
                    </h4>
                )}
            </div>

            <div className='apply-challenge-field-group'>
                <input className='apply-challenge-input'
                    type='text' placeholder='TEAM NAME*'
                    {...register('teamName', {
                        required: 'Team name is required.',
                        validate: (v) => (v?.trim() ? true : 'Team name is required.')
                    })}
                />
                {errors.teamName && (
                    <h4 className='apply-challenge-err-msg'>
                        {errors.teamName.message}
                    </h4>
                )}
            </div>

            <div className='apply-challenge-players-accordion'>
                {Array.from({ length: PLAYER_COUNT }, (_, index) => {
                    const isOpen = expandedPlayerIndexes.includes(index);
                    const panelId = `apply-challenge-player-panel-${index}`;
                    const headerId = `apply-challenge-player-header-${index}`;
                    return (
                        <div className='apply-challenge-player-accordion-item' key={`player-${index}`}>
                            <button type='button'
                                className='apply-challenge-player-accordion-header'
                                id={headerId}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setExpandedPlayerIndexes((prev) => (
                                    prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
                                ))}
                            >
                                <span className='apply-challenge-player-accordion-title'>
                                    PLAYER {index + 1}
                                </span>
                                <span className='apply-challenge-player-accordion-icon' aria-hidden>
                                    {isOpen ? '—' : '+'}
                                </span>
                            </button>

                            <div className='apply-challenge-player-accordion-panel'
                                id={panelId}
                                role='region'
                                aria-labelledby={headerId}
                                hidden={!isOpen}
                            >
                                <div className='apply-challenge-two-col'>
                                    <div className='apply-challenge-field-group'>
                                        <input className='apply-challenge-input'
                                            type='text' placeholder='FIRST NAME*'
                                            {...register(`players.${index}.firstName`, {
                                                required: 'First name is required.'
                                            })}
                                        />
                                        {errors.players?.[index]?.firstName && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].firstName.message}
                                            </h4>
                                        )}
                                    </div>
                                    <div className='apply-challenge-field-group'>
                                        <input className='apply-challenge-input'
                                            type='text' placeholder='LAST NAME*'
                                            {...register(`players.${index}.lastName`, {
                                                required: 'Last name is required.'
                                            })}
                                        />
                                        {errors.players?.[index]?.lastName && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].lastName.message}
                                            </h4>
                                        )}
                                    </div>
                                </div>

                                <div className='apply-challenge-two-col'>
                                    <div className='apply-challenge-field-group apply-challenge-dob-wrap'>
                                        <input type='date'
                                            className={`apply-challenge-input apply-challenge-date${watch(`players.${index}.dob`) ? '' : ' apply-challenge-date--placeholder'}`}
                                            {...register(`players.${index}.dob`, {
                                                required: 'Date of Birth is required.',
                                                validate: validateAge18To25
                                            })}
                                        />
                                        <span className='apply-challenge-dob-label'>
                                            DATE OF BIRTH*
                                        </span>
                                        {errors.players?.[index]?.dob && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].dob.message}
                                            </h4>
                                        )}
                                    </div>
                                    <div className='apply-challenge-field-group'>
                                        <input className='apply-challenge-input'
                                            type='text' placeholder='CONTACT NUMBER*'
                                            {...register(`players.${index}.phone`, {
                                                required: 'Contact number is required.',
                                                pattern: {
                                                    value: Regexp._MOBILE_NUMBER,
                                                    message: 'Please enter a valid telephone number.'
                                                }
                                            })}
                                        />
                                        {errors.players?.[index]?.phone && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].phone.message}
                                            </h4>
                                        )}
                                    </div>
                                </div>

                                <div className='apply-challenge-two-col'>
                                    <div className='apply-challenge-field-group'>
                                        <input className='apply-challenge-input'
                                            type='text' placeholder='E MAIL ADDRESS*'
                                            {...register(`players.${index}.email`, {
                                                required: 'Email is required.',
                                                pattern: {
                                                    value: Regexp._EMAIL,
                                                    message: 'Please enter a valid e-mail address.'
                                                },
                                                validate: (value) => (value?.length > 254 ? 'An email address must not exceed 254 characters.' : true)
                                            })}
                                        />
                                        {errors.players?.[index]?.email && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].email.message}
                                            </h4>
                                        )}
                                    </div>
                                    <div className='apply-challenge-field-group'>
                                        <input type='text' placeholder='ADDRESS*'
                                            className='apply-challenge-input'
                                            {...register(`players.${index}.address`, {
                                                required: 'Address is required.'
                                            })}
                                        />
                                        {errors.players?.[index]?.address && (
                                            <h4 className='apply-challenge-err-msg'>
                                                {errors.players[index].address.message}
                                            </h4>
                                        )}
                                    </div>
                                </div>

                                <div className='apply-challenge-field-group apply-challenge-social-block'>
                                    <p className='apply-challenge-section-label'>
                                        SOCIAL HANDLE
                                    </p>
                                    <div className='apply-challenge-social-grid'>
                                        <div className='apply-challenge-social-cell'>
                                            <input className='apply-challenge-input'
                                                type='text'
                                                placeholder='INSTAGRAM:'
                                                autoComplete='off'
                                                {...register(`players.${index}.instagram`)}
                                            />
                                        </div>
                                        <div className='apply-challenge-social-cell'>
                                            <input className='apply-challenge-input'
                                                type='text'
                                                placeholder='TIK TOK:'
                                                autoComplete='off'
                                                {...register(`players.${index}.tikTok`)}
                                            />
                                        </div>
                                        <div className='apply-challenge-social-cell'>
                                            <input className='apply-challenge-input'
                                                type='text'
                                                placeholder='YOUTUBE:'
                                                autoComplete='off'
                                                {...register(`players.${index}.youTube`)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='apply-challenge-field-group'>
                                    <input className='apply-challenge-input'
                                        type='text' placeholder='WHAT FOOTBALL TEAM DO YOU PLAY FOR? (IF ANY)'
                                        {...register(`players.${index}.footballTeam`)}
                                    />
                                </div>

                                <div className='apply-challenge-field-group apply-challenge-kit-block'>
                                    <p className='apply-challenge-section-label'>
                                        PLEASE CONFIRM YOUR KIT SIZES BELOW
                                    </p>

                                    <div className='apply-challenge-kit-select-row'>
                                        <div className='apply-challenge-kit-select-cell'>
                                            <select
                                                className='apply-challenge-select'
                                                {...register(`players.${index}.jerseySize`, {
                                                    required: 'Jersey size is required.'
                                                })}
                                            >
                                                <option value=''>JERSEY TOP*</option>
                                                {KIT_SIZES.map((size) => (
                                                    <option key={`jersey-${index}-${size}`} value={size}>
                                                        {size.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.players?.[index]?.jerseySize && (
                                                <h4 className='apply-challenge-err-msg'>
                                                    {errors.players[index].jerseySize.message}
                                                </h4>
                                            )}
                                        </div>
                                        <div className='apply-challenge-kit-select-cell'>
                                            <select
                                                className='apply-challenge-select'
                                                {...register(`players.${index}.shortsSize`, {
                                                    required: 'Shorts size is required.'
                                                })}
                                            >
                                                <option value=''>SHORTS*</option>
                                                {KIT_SIZES.map((size) => (
                                                    <option key={`shorts-${index}-${size}`} value={size}>
                                                        {size.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.players?.[index]?.shortsSize && (
                                                <h4 className='apply-challenge-err-msg'>
                                                    {errors.players[index].shortsSize.message}
                                                </h4>
                                            )}
                                        </div>
                                        <div className='apply-challenge-kit-select-cell'>
                                            <input
                                                className='apply-challenge-input apply-challenge-input--boot'
                                                type='number'
                                                step='0.5'
                                                min='0'
                                                placeholder='BOOT SIZE*'
                                                {...register(`players.${index}.bootSize`, {
                                                    required: 'Boot size is required.',
                                                    validate: (v) => {
                                                        const n = Number(v);
                                                        if (v === '' || v === undefined || Number.isNaN(n) || n <= 0) {
                                                            return 'Enter a valid boot size.';
                                                        }
                                                        return true;
                                                    }
                                                })}
                                            />
                                            {errors.players?.[index]?.bootSize && (
                                                <h4 className='apply-challenge-err-msg'>
                                                    {errors.players[index].bootSize.message}
                                                </h4>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className='apply-challenge-confirm-block'>
                <div className='apply-challenge-field-group'>
                    <p className='apply-challenge-confirm-q'>
                        Can you confirm your entire team currently have a UK passport or passport with relevant visa that will allow you to travel to New York, USA?*
                    </p>
                    <div className='apply-challenge-radio-row'>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='yes'
                                {...register('confirmPassport', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>YES</span>
                        </label>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='no'
                                {...register('confirmPassport', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>NO</span>
                        </label>
                    </div>
                    {errors.confirmPassport && <h4 className='apply-challenge-err-msg'>
                        {errors.confirmPassport.message}
                    </h4>}
                </div>

                <div className='apply-challenge-field-group'>
                    <p className='apply-challenge-confirm-q'>
                        Can you confirm your entire team do not have any criminal convictions that will not allow you to travel or stay in the USA?*
                    </p>
                    <div className='apply-challenge-radio-row'>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='yes'
                                {...register('confirmCriminal', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>YES</span>
                        </label>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='no'
                                {...register('confirmCriminal', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>NO</span>
                        </label>
                    </div>
                    {errors.confirmCriminal && (
                        <h4 className='apply-challenge-err-msg'>
                            {errors.confirmCriminal.message}
                        </h4>
                    )}
                </div>

                <div className='apply-challenge-field-group'>
                    <p className='apply-challenge-confirm-q'>
                        Can you confirm your entire team are available to travel between the 22nd and 28th June 2026 to New York, USA?*
                    </p>
                    <div className='apply-challenge-radio-row'>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='yes'
                                {...register('confirmTravel', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>YES</span>
                        </label>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='no'
                                {...register('confirmTravel', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>NO</span>
                        </label>
                    </div>
                    {errors.confirmTravel && (
                        <h4 className='apply-challenge-err-msg'>
                            {errors.confirmTravel.message}
                        </h4>
                    )}
                </div>

                <div className='apply-challenge-field-group'>
                    <p className='apply-challenge-confirm-q'>
                        Can you confirm no individual within this team has entered in another team for the 3v3 challenge?*
                    </p>
                    <div className='apply-challenge-radio-row'>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='yes'
                                {...register('confirmNoDoubleEntry', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>YES</span>
                        </label>
                        <label className='apply-challenge-radio-label'>
                            <input className='apply-challenge-radio-input' type='radio' value='no'
                                {...register('confirmNoDoubleEntry', { required: 'Required.' })}
                            />
                            <span className='apply-challenge-radio-text'>NO</span>
                        </label>
                    </div>
                    {errors.confirmNoDoubleEntry && (
                        <h4 className='apply-challenge-err-msg'>
                            {errors.confirmNoDoubleEntry.message}
                        </h4>
                    )}
                </div>
            </div>

            <div className='apply-challenge-field-group apply-challenge-signup-news-row'>
                <div className='apply-challenge-terms-container'>
                    <div className='apply-challenge-terms-wrapper'>
                        <div className='apply-challenge-terms-checkbox mt-adjust'>
                            <label htmlFor='apply-challenge-signup-for-news'>
                                {isSignupForNews ? (
                                    <svg className='apply-challenge-terms-checkbox-icon' width='34' height='34' viewBox='0 0 162 162' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                                        <g>
                                            <path d='M132.319 0H29.6772L0 29.6772V132.319L29.6772 161.933H132.319L161.933 132.319V29.6772L132.319 0Z' fill='#DAFF00' />
                                            <path d='M123.687 44.5474H115.243L65.4032 94.3875L46.8156 75.7368H38.2463L33.0796 80.9036V89.3468L61.1186 117.386H69.6248L128.664 58.2203L128.727 56.9602C128.79 54.6288 128.79 53.3056 128.79 51.0373V49.7141L123.687 44.5474Z' fill='#282727' />
                                        </g>
                                    </svg>
                                ) : (
                                    <svg className='apply-challenge-terms-checkbox-icon' width='34' height='34' viewBox='0 0 34 34' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                                        <path d='M27.7592 0H6.25409L0 6.25409V27.7592L6.25409 34H27.7592L34 27.7592V6.25409L27.7592 0ZM8.34762 28.9119L5.08808 25.6524V8.34762L8.34762 5.08808H25.6391L28.8987 8.34762V25.6391L25.6391 28.8987H8.34762V28.9119Z' fill='#B3B4B4' />
                                    </svg>
                                )}
                            </label>
                            <input type='checkbox' id='apply-challenge-signup-for-news'
                                className='apply-challenge-terms-checkbox-input'
                                {...register('signupForNews')}
                            />
                        </div>
                        <p className='apply-challenge-signup-news-copy'>
                            Sign up for news, exclusives and more from Stormzy. Emails will be sent by or on behalf of
                            Universal Music Operations Ltd. You may withdraw your consent at any time. See Privacy Policy at{' '}
                            <a href='https://privacy.umusic.com/uk/' target='_blank'
                                rel='noopener noreferrer'
                                className='apply-challenge-signup-news-link'
                            >
                                https://privacy.umusic.com/uk/
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </div>

            <div className='apply-challenge-field-group apply-challenge-tc-row'>
                <div className='apply-challenge-terms-container'>
                    <div className='apply-challenge-terms-wrapper'>
                        <div className='apply-challenge-terms-checkbox'>
                            <label htmlFor='apply-challenge-agree-terms'>
                                {isTermsAccepted ? (
                                    <svg className='apply-challenge-terms-checkbox-icon' width='34' height='34' viewBox='0 0 162 162' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                                        <g>
                                            <path d='M132.319 0H29.6772L0 29.6772V132.319L29.6772 161.933H132.319L161.933 132.319V29.6772L132.319 0Z' fill='#DAFF00' />
                                            <path d='M123.687 44.5474H115.243L65.4032 94.3875L46.8156 75.7368H38.2463L33.0796 80.9036V89.3468L61.1186 117.386H69.6248L128.664 58.2203L128.727 56.9602C128.79 54.6288 128.79 53.3056 128.79 51.0373V49.7141L123.687 44.5474Z' fill='#282727' />
                                        </g>
                                    </svg>
                                ) : (
                                    <svg className='apply-challenge-terms-checkbox-icon' width='34' height='34' viewBox='0 0 34 34' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                                        <path d='M27.7592 0H6.25409L0 6.25409V27.7592L6.25409 34H27.7592L34 27.7592V6.25409L27.7592 0ZM8.34762 28.9119L5.08808 25.6524V8.34762L8.34762 5.08808H25.6391L28.8987 8.34762V25.6391L25.6391 28.8987H8.34762V28.9119Z' fill='#B3B4B4' />
                                    </svg>
                                )}
                            </label>
                            <input type='checkbox' id='apply-challenge-agree-terms'
                                className='apply-challenge-terms-checkbox-input'
                                checked={isTermsAccepted}
                                readOnly
                                onClick={onAgreeTermsCheckboxClick}
                                {...register('agreeTerms', {
                                    validate: (v) => v === true || 'You must agree to the Terms and Conditions.'
                                })}
                            />
                        </div>
                        <h4 className='apply-challenge-terms-copy'>
                            Please confirm you have read and agree with the{' '}
                            <button type='button' onClick={() => setIsTermsModalOpen(true)}
                                className='apply-challenge-terms-copy-link'
                            >
                                terms and conditions
                            </button>
                        </h4>
                    </div>
                    {errors.agreeTerms && (
                        <h4 className='apply-challenge-err-msg'>
                            {errors.agreeTerms.message}
                        </h4>
                    )}
                </div>
            </div>

            <div className='apply-challenge-form-action'>
                <button className='apply-challenge-submit-btn' type='submit' disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
            </div>

            <TermsAgreementModal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                onAgree={() => {
                    setValue('agreeTerms', true, { shouldValidate: true, shouldDirty: true });
                    clearErrors('agreeTerms');
                }}
            />
        </form>
    );
};


export default ECupApplyChallengeForm;